import React, { useEffect, useState } from 'react';

import styled, { useTheme } from 'styled-components';

import { FormattedMessage } from '@techstyle/react-intl';

import { desktop } from '../../styles';
import { BasicButton } from '../../styles/button';
import { Component as Price } from '../ExtendedPrice';
import { Component as Loading } from '../Loading';
import MembershipFooterDisclaimer from '../MembershipFooterDisclaimer/MembershipFooterDisclaimer';

import { MembershipFeaturesList } from './MembershipFeaturesList';
import { Content, Section, Subtitle, Title } from './shared';

const WhiteLogo = styled.img.attrs({
  src: '/static/images/x-logo-white.svg',
  alt: 'Savage X Fenty',
})`
  width: 54px;
  height: 54px;
`;

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Separator = styled.div`
  width: 275px;
  height: 1px;
  background-color: ${({ theme }) => theme.colors.white};
`;

const PayAsGuestButton = styled(BasicButton).attrs({
  fullWidth: true,
  variant: 'secondaryLight',
})`
  display: block;
  margin-top: -8px;

  ${desktop`
    display: none;
  `}
`;

const StrikethroughPrice = styled(Price)`
  color: ${({ theme }) => theme.colors.stone};
  text-decoration: line-through;
`;

const Disclaimer = styled.div`
  color: ${({ theme }) => theme.colors.white};
`;

interface MemberSectionProps {
  guestPrice: number;
  memberPrice: number;
  membershipPrice: number;
  onLearnMoreClick: () => void;
  onContinueAsMember: () => void;
  onContinueAsGuest: () => void;
}
const SubtitleColor = styled.span`
  color: ${({ theme }) => theme.colors.lavender};
  font-weight: 700;
`;
const MEMBER_SECTION_BG_IMAGE =
  '/static/images/membership_modal/background.png';

export const MemberSection = ({
  guestPrice,
  memberPrice,
  onLearnMoreClick,
  onContinueAsMember,
  onContinueAsGuest,
}: MemberSectionProps) => {
  const theme = useTheme();
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    const img = new Image();
    img.src = MEMBER_SECTION_BG_IMAGE;
    img.onload = () => setImageLoaded(true);
  }, []);

  const memberDiscountPercentage = Math.trunc(
    ((guestPrice - memberPrice) / guestPrice) * 100
  );

  if (!imageLoaded) {
    return <Loading />;
  }

  return (
    <Section imageBackground={MEMBER_SECTION_BG_IMAGE}>
      <Content>
        <WhiteLogo />
        <TitleSection>
          <Title color={theme.colors.white}>
            <FormattedMessage
              id="global_checkout.membership_modal_main_title"
              defaultMessage="Pay only {newPrice} {oldPrice} with rewards Membership"
              values={{
                newPrice: <Price amount={memberPrice} />,
                oldPrice: (
                  <>
                    <StrikethroughPrice amount={guestPrice} />
                    <br />
                  </>
                ),
              }}
            />
          </Title>
          <Subtitle color={theme.colors.white}>
            <FormattedMessage
              id="global_checkout.membership_modal_subtitle"
              defaultMessage="{saveOnThisOrder} and enjoy Everyday Membership Perks anytime in the future"
              values={{
                saveOnThisOrder: (
                  <SubtitleColor>
                    <FormattedMessage
                      id="global_checkout.membership_modal_save_on_this_order"
                      defaultMessage="Save {memberDiscountPercentage}% on your first order"
                      values={{
                        memberDiscountPercentage,
                      }}
                    />
                  </SubtitleColor>
                ),
              }}
            />
          </Subtitle>
        </TitleSection>
        <Separator />
        <MembershipFeaturesList />
        {/* @ts-ignore */}
        <BasicButton
          fullWidth
          variant="primaryLavender"
          onClick={onContinueAsMember}
        >
          <FormattedMessage
            defaultMessage="BECOME A MEMBER"
            id="global_checkout.membership_modal_member_button"
          />
        </BasicButton>
        {/* @ts-ignore */}
        <PayAsGuestButton onClick={onContinueAsGuest}>
          <FormattedMessage
            defaultMessage="CONTINUE AS A GUEST"
            id="global_checkout.membership_modal_guest_button"
          />
        </PayAsGuestButton>
        <Disclaimer>
          <MembershipFooterDisclaimer
            onLearnMoreClick={onLearnMoreClick}
            membershipOptInVariant="modal"
          />
        </Disclaimer>
      </Content>
    </Section>
  );
};
